import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearUser } from '../../store/userSlice';

import axios from "axios";
import config from '../../config';

import '../../styles/sidebar.css';

const API_URL = config.API_URL || 'http://localhost:5000';

const Sidebar = ({ isCollapsed, toggleSidebar, User }) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const role = User?.user?.active ? Object.keys(User.user.active)[0] : null;

  const [activeItem, setActiveItem] = useState(location.pathname);
  const [expandedItem, setExpandedItem] = useState(null);
  const [showSubMenu, setShowSubMenu] = useState(false);

  const menuItems = useMemo(() => {
    // Dashboard or Home
    const dashboardItem = { name: 'Dashboard', path: '/dashboard', icon: 'fas fa-chart-simple', roles: ['manager'] };
    const homeItem = { name: 'Home', path: '/dashboard', icon: 'fas fa-home', roles: ['owner', 'tenant'] };

    // Units
    const unitsItem = { name: 'Units', path: '/properties', icon: 'fas fa-building', roles: ['manager'] };

    // Accounting
    const accountingItem = {
      name: 'Accounting',
      icon: 'fas fa-file-invoice-dollar',
      subItems: [
        {
          name: 'Property',
          subSubItems: [{ name: 'Property Portfolio', path: '/portfolio' }]
        },
        {
          name: 'Accounts',
          subSubItems: [{ name: 'Balance and Transactions', path: '/balance-transactions' }]
        },
        {
          name: 'Manage',
          subSubItems: [
            { name: 'Manage Vendors', path: '/manage-vendors' },
            { name: 'Manage Invoice Categories', path: '/manage-invoice-categories' }
          ]
        }
      ],
      roles: ['manager']
    };

    const balanceTransactionsItem = {
      name: 'Balance and Transactions',
      path: '/balance-transactions',
      icon: 'fas fa-receipt',
      roles: ['owner', 'tenant']
    };

    // Service Requests
    const serviceRequestsManager = {
      name: 'Service Requests',
      icon: 'fas fa-helmet-safety',
      subItems: [
        {
          name: 'Services',
          subSubItems: [
            { name: 'Requests', path: '/requests/requests' },
            { name: 'Gate Pass', path: '/requests/gate-pass' },
            { name: 'Work Permit', path: '/requests/work-permit' },
            { name: 'Amenities', path: '/requests/amenities' },
          ]
        },
      ],
      roles: ['manager']
    };

    // Service Requests for Owner and Tenant
    const serviceRequestsTenant = {
      name: 'Requests, Permits, and Bookings',
      icon: 'fas fa-clipboard',
      subItems: [
        {
          name: 'Services',
          subSubItems: [
            { name: 'Requests', path: '/requests/requests' },
            { name: 'Gate Pass', path: '/requests/gate-pass' },
            { name: 'Work Permit', path: '/requests/work-permit' },
            { name: 'Amenities', path: '/requests/amenities' },
          ]
        },
      ],
      roles: ['owner', 'tenant']
    };

    // Parcels and Visitors
    const parcelsItem = { name: 'Parcels', path: '/parcels', icon: 'fas fa-boxes-stacked', roles: ['manager'] };
    const visitorsItem = { name: 'Visitors', path: '/visitors', icon: 'fas fa-users', roles: ['manager'] };
    const parcelsVisitorsItem = { name: 'Parcels and Visitors', path: '/parcels-visitors', icon: 'fas fa-boxes-packing', roles: ['owner', 'tenant'] };

    // Announcements
    const announcementsItem = { name: 'Announcements', path: '/announcements', icon: 'fas fa-bullhorn', roles: ['manager', 'owner', 'tenant'] };

    // People
    const peopleItem = {
      name: 'People',
      icon: 'fas fa-building-user',
      subItems: [
        {
          name: 'Staff',
          subSubItems: [{ name: 'Staff and Management', path: '/management' }]
        },
        {
          name: 'Owners and Tenants',
          subSubItems: [
            { name: 'Owners', path: '/owners' },
            { name: 'Tenants', path: '/tenants' }
          ]
        }
      ],
      roles: ['manager']
    };

    // Ellie AI
    const ellieAIItem = { name: 'Ellie AI', path: '/aichat', icon: 'fas fa-comment', roles: ['manager', 'owner', 'tenant'] };

    // Assemble by role
    let items = [];

    if (role === 'manager') {
      items = [
        dashboardItem,
        unitsItem,
        accountingItem,
        serviceRequestsManager,
        parcelsItem,
        visitorsItem,
        peopleItem,
        announcementsItem,
        ellieAIItem
      ];
    } else if (role === 'owner' || role === 'tenant') {
      items = [
        homeItem,
        balanceTransactionsItem,
        serviceRequestsTenant,
        parcelsVisitorsItem,
        announcementsItem,
        ellieAIItem
      ];
    }

    return items;
  }, [role]);

  useEffect(() => {
    setActiveItem(location.pathname);

    if (isCollapsed) {
      setExpandedItem(null);
      setShowSubMenu(false);
    } else {
      const parentMenu = menuItems.find((item) =>
        item.subItems?.some((subItem) =>
          subItem.subSubItems?.some((subSubItem) => subSubItem.path === location.pathname)
        )
      );

      if (parentMenu) {
        setExpandedItem(parentMenu.name);
        setShowSubMenu(true);
      } else {
        setShowSubMenu(false);
        setExpandedItem(null);
      }
    }
  }, [location, isCollapsed, menuItems]);

  const toggleSubMenu = (itemName) => {
    if (isCollapsed) {
      toggleSidebar();
    }

    setTimeout(() => {
      setExpandedItem(itemName);
      setShowSubMenu(true);
    }, 0);

    if (expandedItem === itemName) {
      setExpandedItem(null);
      setShowSubMenu(false);
    } else {
      setExpandedItem(itemName);
      setShowSubMenu(true);
    }
  };

  const toggleMainMenu = () => {
    menuItems.some((item) =>
      item.subItems?.some((subItem) =>
        subItem.subSubItems?.some((subSubItem) => subSubItem.path === location.pathname)
      )
    );

    setExpandedItem(null);
    setShowSubMenu(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(
        `${API_URL}/v2/auth/logout`,
        {},
        {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}}
      );

      // Clear user from Redux store
      dispatch(clearUser());

      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} sm:fixed`}>
      <div className="sidebar-header">
        <div className="toggle-button" onClick={toggleSidebar} aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
          <i className="fa-solid fa-bars"></i>
        </div>
        <div className="logo">
          {!isCollapsed && <img src="/alt-logo/yellow-plain.png" alt="GoCondo Logo" className="logo-image" />}
        </div>
      </div>
      <nav className="sidebar-nav">
        <ul>
          {!showSubMenu ? (
            menuItems
              .filter((item) => item.roles?.includes(role))
              .map((item, index) => (
                <li key={index}>
                  {item.subItems ? (
                    <div
                      className={`sidebar-link ${expandedItem === item.name ||
                        item.subItems?.some(subItem =>
                          subItem.subSubItems?.some(subSubItem => subSubItem.path === activeItem)
                        )
                        ? 'active'
                        : ''
                        }`}
                      onClick={() => toggleSubMenu(item.name)}
                    >
                      <i className={`${item.icon} ${expandedItem === item.name ||
                        item.subItems?.some(subItem =>
                          subItem.subSubItems?.some(subSubItem => subSubItem.path === activeItem)
                        )
                        ? 'active-icon'
                        : ''
                        }`}></i>
                      <div>
                        {!isCollapsed && item.name}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`sidebar-link ${activeItem === item.path ? 'active' : ''}`}
                      onClick={() => { setActiveItem(item.path) }}
                    >
                      <i className={item.icon}></i>
                      <div>
                        {!isCollapsed && item.name}
                      </div>
                    </Link>
                  )}
                </li>
              ))
          ) : (
            menuItems
              .filter((item) => item.name === expandedItem)
              .map((item, index) => (
                <div key={index}>
                  <div className="sidebar-link-name"><i className="fa-solid fa-arrow-left" onClick={toggleMainMenu}></i>{item.name}</div>
                  {item.subItems.map((subItem, subIndex) => (
                    <li key={subIndex}>
                      <div className="sidebar-submenu">{subItem.name}</div>
                      <ul className="subsubmenu">
                        {subItem.subSubItems.map((subSubItem, subSubIndex) => (
                          <li key={subSubIndex}>
                            <Link
                              to={subSubItem.path}
                              className={`sidebar-subsubmenu ${activeItem === subSubItem.path ? 'active' : ''}`}
                              onClick={() => {
                                setActiveItem(subSubItem.path);
                                setExpandedItem(item.name);
                              }}
                            >
                              {subSubItem.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </div>
              ))
          )}
        </ul>
      </nav>
      <button className="logout-button" onClick={handleLogout}>
        <i className="fa-solid fa-sign-out"></i>
        {!isCollapsed && 'Log Out'}
      </button>
    </aside>
  );
};

export default Sidebar;
